import { Controller } from '@hotwired/stimulus';

// Sortable.js is loaded via CDN as a global variable

export default class extends Controller {
  static values = {
    templateId: String,
    autoSaveUrl: String
  }

  static targets = [
    'layoutArea',
    'initialDropzone'
  ]

  static outlets = ['layout-autosave']

  connect() {
    console.log('🎯 LayoutBuilder Controller Connected!');
    console.log('📊 Values:', {
      templateId: this.templateIdValue,
      autoSaveUrl: this.autoSaveUrlValue
    });
    console.log('🎪 Targets found:', {
      layoutArea: this.hasLayoutAreaTarget,
      initialDropzone: this.hasInitialDropzoneTarget
    });
    console.log('🔌 Outlets connected:', {
      layoutAutosave: this.hasLayoutAutosaveOutlet
    });

    // Check if Sortable.js is available
    if (typeof Sortable === 'undefined') {
      console.error('❌ Sortable.js is not loaded! Drag and drop will not work.');
      this.showDebugError('Sortable.js not loaded');
      return;
    } else {
      console.log('✅ Sortable.js is available');
    }

    this.rowCounter = 0;
    this.debugMode = false; // Disable debug mode
    this.sortableInstances = []; // Track sortable instances for debugging

    this.initializeDragAndDrop();
    this.loadExistingLayout();
    this.bindEvents();
  }

  disconnect() {
    console.log('🚪 LayoutBuilder Controller Disconnecting');

    // Clear any pending timeouts to prevent memory leaks
    if (this.positionUpdateTimeout) {
      clearTimeout(this.positionUpdateTimeout);
    }
    if (this.fieldDropTimeout) {
      clearTimeout(this.fieldDropTimeout);
    }
    if (this.sectionSaveTimeout) {
      clearTimeout(this.sectionSaveTimeout);
    }

    this.cleanupSortables();
    this.removeDebugPanel();
  }

  showDebugError(message) {
    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #dc3545;
      color: white;
      padding: 15px;
      border-radius: 5px;
      z-index: 9999;
      font-weight: bold;
    `;
    errorDiv.textContent = `Debug Error: ${message}`;
    document.body.appendChild(errorDiv);

    setTimeout(() => errorDiv.remove(), 5000);
  }

  createDebugPanel() {
    if (!this.debugMode) return;

    const debugPanel = document.createElement('div');
    debugPanel.id = 'layout-builder-debug';
    debugPanel.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 20px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 15px;
      border-radius: 5px;
      font-family: monospace;
      font-size: 12px;
      z-index: 9998;
      max-width: 300px;
      max-height: 200px;
      overflow-y: auto;
    `;

    debugPanel.innerHTML = `
      <div><strong>🔧 Layout Builder Debug</strong></div>
      <div>Controllers: <span id="debug-controllers">Loading...</span></div>
      <div>Sortables: <span id="debug-sortables">0</span></div>
      <div>Fields: <span id="debug-fields">0</span></div>
      <div>Rows: <span id="debug-rows">0</span></div>
      <div>Sortable.js: <span id="debug-sortablejs">${typeof Sortable !== 'undefined' ? '✅' : '❌'}</span></div>
      <div style="margin-top: 8px;">
        <button onclick="window.layoutBuilderDebug()" style="margin-right: 5px;">🐛 Debug</button>
        <button onclick="window.testDragAndDrop()" style="margin-right: 5px;">🎯 Test D&D</button>
        <button onclick="window.reinitializeDragAndDrop()">🔄 Reinit</button>
      </div>
    `;

    document.body.appendChild(debugPanel);
    this.updateDebugPanel();

    // Add global debug functions
    window.layoutBuilderDebug = () => {
      console.log('🔧 Layout Builder Debug Info:');
      console.log('Controller instance:', this);
      console.log('Sortable instances:', this.sortableInstances);
      console.log('Current fields:', this.getFieldsData());
      console.log('DOM elements:', {
        layoutArea: this.layoutAreaTarget,
        initialDropzone: this.initialDropzoneTarget
      });
      console.log('Sortable.js available:', typeof Sortable !== 'undefined');

      // Test if elements have proper data attributes
      console.log('Controller data attributes:', {
        layoutBuilder: this.element.dataset.controller?.includes('layout-builder'),
        layoutAutosave: this.element.dataset.controller?.includes('layout-autosave')
      });
    };

    window.testDragAndDrop = () => {
      console.log('🎯 Testing drag and drop functionality...');


      // Test existing rows
      const existingRows = this.layoutAreaTarget?.querySelectorAll('.layout-row');
      console.log('Existing rows:', existingRows?.length);

      // Test if sortables are properly initialized
      console.log('Sortable instances initialized:', this.sortableInstances.length);
      this.sortableInstances.forEach((sortable, index) => {
        console.log(`  ${index + 1}. ${sortable.name}:`, sortable.instance ? '✅' : '❌');
      });
    };

    window.reinitializeDragAndDrop = () => {
      console.log('🔄 Reinitializing drag and drop...');
      this.cleanupSortables();
      this.sortableInstances = [];
      this.initializeDragAndDrop();
      this.updateDebugPanel();
      console.log('✅ Reinitialization complete');
    };
  }

  updateDebugPanel() {
    const controllers = document.querySelectorAll('[data-controller*="layout"]').length;
    const sortables = this.sortableInstances.length;
    const fields = document.querySelectorAll('.layout-field').length;
    const rows = document.querySelectorAll('.layout-row').length;

    const debugControllers = document.getElementById('debug-controllers');
    const debugSortables = document.getElementById('debug-sortables');
    const debugFields = document.getElementById('debug-fields');
    const debugRows = document.getElementById('debug-rows');

    if (debugControllers) debugControllers.textContent = controllers;
    if (debugSortables) debugSortables.textContent = sortables;
    if (debugFields) debugFields.textContent = fields;
    if (debugRows) debugRows.textContent = rows;
  }

  removeDebugPanel() {
    const debugPanel = document.getElementById('layout-builder-debug');
    if (debugPanel) debugPanel.remove();
    delete window.layoutBuilderDebug;
    delete window.testDragAndDrop;
    delete window.reinitializeDragAndDrop;
  }

  bindEvents() {
    // Row template buttons (exclude section headers)
    document.querySelectorAll('.template-option-bottom').forEach(option => {
      option.addEventListener('click', (e) => {
        const template = e.currentTarget.dataset.template;
        const type = e.currentTarget.dataset.type;

        // Only create rows for actual row templates, not section headers
        if (template && type !== 'section') {
          this.addNewRowWithTemplate(template);
        }
      });
    });

    // Add first row button
    const addFirstRowBtn = document.getElementById('add-first-row-btn');
    if (addFirstRowBtn) {
      addFirstRowBtn.addEventListener('click', () => {
        this.addNewRowWithTemplate('1x12');
      });
    }
  }

  initializeDragAndDrop() {
    console.log('🎪 Initializing drag and drop...');
    this.initializeLayoutArea();
    this.updateDebugPanel();
  }


  initializeLayoutArea() {
    console.log('🏗️ Initializing layout area...');

    if (!this.hasLayoutAreaTarget) {
      console.error('❌ Layout area target not found!');
      return;
    }

    console.log('✅ Layout area target found:', this.layoutAreaTarget);
    console.log('📦 Existing rows in layout area:', this.layoutAreaTarget.querySelectorAll('.layout-row').length);

    // Note: No drag and drop for rows and section headers - using up/down buttons instead
    console.log('📝 Layout area initialized without drag and drop (using arrow buttons for reordering)');
  }


  initializeColumnSortable(columnElement) {
    const columnId = columnElement.dataset.columnId;
    console.log(`📋 Initializing column sortable for: ${columnId}`);

    const columnContent = columnElement.querySelector('.layout-column-content');
    if (!columnContent) {
      console.error(`❌ Column content not found for: ${columnId}`);
      return;
    }

    try {
      // Make fields within this column sortable
      const columnSortable = Sortable.create(columnContent, {
        group: 'fields',
        animation: 150,
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',
        onAdd: (evt) => {
          console.log(`🔥 Field added to column: ${columnId}`, evt.item);
          this.handleFieldDrop(evt, columnElement);
        },
        onUpdate: (evt) => {
          console.log(`📝 Field updated in column: ${columnId}`, evt.item);
          this.updateFieldPositions(columnElement);
        },
        onStart: (evt) => {
          console.log(`🎯 Field drag started in column: ${columnId}`, evt.item);
        },
        onEnd: (evt) => {
          console.log(`🏁 Field drag ended in column: ${columnId}`, evt.item);
        }
      });

      this.sortableInstances.push({
        name: `column-${columnId}`,
        instance: columnSortable,
        element: columnContent
      });

      console.log(`✅ Column sortable created for: ${columnId}`);
    } catch (error) {
      console.error(`❌ Error creating column sortable for ${columnId}:`, error);
    }
  }

  addNewRowWithTemplate(template) {
    // Find the next available row number by checking existing rows
    const existingRowNumbers = this.getExistingRowNumbers();
    const nextRowNumber = this.findNextAvailableRowNumber(existingRowNumbers);

    console.log(`🔢 Existing row numbers: [${existingRowNumbers.join(', ')}]`);
    console.log(`🆕 Creating new row with number: ${nextRowNumber}`);

    const rowId = `row_${nextRowNumber}`;

    // Hide initial dropzone
    if (this.hasInitialDropzoneTarget) {
      this.initialDropzoneTarget.style.display = 'none';
    }

    const rowElement = this.createRowFromTemplate(rowId, template, nextRowNumber);
    this.layoutAreaTarget.appendChild(rowElement);

    this.initializeColumnsInRow(rowElement);

    // Update row counter to the highest number used
    this.rowCounter = Math.max(this.rowCounter, nextRowNumber);

    // Scroll to new row
    rowElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  createRowFromTemplate(rowId, template, rowNumber) {
    const rowDiv = document.createElement('div');
    rowDiv.className = 'layout-row mb-3';
    rowDiv.dataset.rowId = rowId;
    rowDiv.dataset.template = template;

    const columns = this.getColumnsForTemplate(template);

    rowDiv.innerHTML = `
      <div class="row-left-controls">
        <div class="row-move-actions">
          <button class="btn btn-sm btn-outline-info" data-action="click->layout-builder#moveRowUp" data-row-id="${rowId}" title="Move Row Up">
            <i class="fas fa-chevron-up"></i>
          </button>
          <button class="btn btn-sm btn-outline-info" data-action="click->layout-builder#moveRowDown" data-row-id="${rowId}" title="Move Row Down">
            <i class="fas fa-chevron-down"></i>
          </button>
        </div>
      </div>
      <div class="row-main-content">
        <div class="row-content row g-2">
          ${columns.map((colWidth, index) => `
            <div class="layout-column col-${colWidth}" data-column-id="${rowId}_col_${index + 1}" data-width-ratio="${colWidth}">
              <div class="layout-column-content">
                <div class="column-placeholder text-center text-muted py-3">
                  <i class="fas fa-mouse-pointer"></i>
                  <div>Drop fields here</div>
                </div>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
      <div class="row-right-controls">
        <div class="row-actions">
          <button class="btn btn-sm btn-outline-secondary" data-action="click->layout-builder#showTemplateSelector" data-row-id="${rowId}" title="Change Layout">
            <i class="fas fa-columns"></i>
          </button>
          <button class="btn btn-sm btn-outline-danger" data-action="click->layout-builder#deleteRow" data-row-id="${rowId}" title="Delete Row">
            <i class="fas fa-trash"></i>
          </button>
        </div>
        <div class="template-selector" style="display: none;">
          <div class="template-options">
            ${['1x12', '2x6', '3x4', '4x3'].map(tmpl => `
              <button class="btn btn-sm btn-outline-primary me-1" data-action="click->layout-builder#changeRowTemplate" data-row-id="${rowId}" data-template="${tmpl}">
                ${tmpl.replace('x', ' × ')}
              </button>
            `).join('')}
          </div>
        </div>
      </div>
    `;

    return rowDiv;
  }

  getColumnsForTemplate(template) {
    const templates = {
      '1x12': [12],
      '2x6': [6, 6],
      '3x4': [4, 4, 4],
      '4x3': [3, 3, 3, 3]
    };
    return templates[template] || [12];
  }

  getExistingRowNumbers() {
    const rowNumbers = [];

    // Get numbers from existing server-rendered rows (existing_row_X)
    const existingRows = this.layoutAreaTarget.querySelectorAll('[data-row-id^="existing_row_"]');
    existingRows.forEach(row => {
      const match = row.dataset.rowId.match(/existing_row_(\d+)/);
      if (match) {
        rowNumbers.push(parseInt(match[1]));
      }
    });

    // Get numbers from JavaScript-created rows (row_X)
    const jsRows = this.layoutAreaTarget.querySelectorAll('[data-row-id^="row_"]');
    jsRows.forEach(row => {
      const match = row.dataset.rowId.match(/row_(\d+)/);
      if (match) {
        rowNumbers.push(parseInt(match[1]));
      }
    });

    return rowNumbers.sort((a, b) => a - b);
  }

  findNextAvailableRowNumber(existingNumbers) {
    if (existingNumbers.length === 0) {
      return 1;
    }

    // Find the first gap in the sequence, or return the next number after the highest
    for (let i = 1; i <= Math.max(...existingNumbers) + 1; i++) {
      if (!existingNumbers.includes(i)) {
        return i;
      }
    }

    // Fallback: return the next number after the highest
    return Math.max(...existingNumbers) + 1;
  }

  initializeColumnsInRow(rowElement) {
    const columns = rowElement.querySelectorAll('.layout-column');
    columns.forEach(column => {
      this.initializeColumnSortable(column);
    });
  }

  // Add new row below a specific row
  addRowBelow(event) {
    const rowId = event.currentTarget.dataset.rowId;
    const targetRow = this.layoutAreaTarget.querySelector(`[data-row-id="${rowId}"]`);

    if (!targetRow) {
      console.error(`❌ Target row not found: ${rowId}`);
      return;
    }

    console.log(`➕ Adding new row below ${rowId}`);

    // Create new row and insert it after the target row
    const newRowElement = this.createNewRowAtPosition('2x6', targetRow);

    // Scroll to new row
    newRowElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  // Helper method to create a new row at a specific position
  createNewRowAtPosition(template, referenceElement) {
    const existingRowNumbers = this.getExistingRowNumbers();
    const nextRowNumber = this.findNextAvailableRowNumber(existingRowNumbers);
    const rowId = `row_${nextRowNumber}`;

    // Hide initial dropzone if visible
    if (this.hasInitialDropzoneTarget) {
      this.initialDropzoneTarget.style.display = 'none';
    }

    // Create the row element
    const rowElement = this.createRowFromTemplate(rowId, template, nextRowNumber);

    // Create the add-row-below container and append it to the row element
    const addRowBelowContainer = document.createElement('div');
    addRowBelowContainer.className = 'add-row-below';
    addRowBelowContainer.innerHTML = `
      <button class="btn btn-outline-secondary" data-action="click->layout-builder#addRowBelow" data-row-id="${rowId}" title="Add Row Below">
        + row
      </button>
    `;

    // Append the add-row-below button to the row element itself
    rowElement.appendChild(addRowBelowContainer);

    // Insert the new row structure after the reference element
    const nextSibling = referenceElement.nextSibling;
    if (nextSibling) {
      referenceElement.parentNode.insertBefore(rowElement, nextSibling);
    } else {
      referenceElement.parentNode.appendChild(rowElement);
    }

    // Initialize the columns in the new row
    this.initializeColumnsInRow(rowElement);

    // Update row counter
    this.rowCounter = Math.max(this.rowCounter, nextRowNumber);

    return rowElement;
  }

  handleFieldDrop(evt, columnElement) {
    const fieldElement = evt.item;
    const fieldId = fieldElement.dataset.fieldId;

    if (!fieldId) return;

    // Cache DOM queries for better performance
    const rowElement = columnElement.closest('.layout-row');
    const rowId = rowElement.dataset.rowId;

    // Use more efficient method to get column index
    const columnIndex = parseInt(columnElement.dataset.columnId.split('_col_')[1]) - 1;
    const widthRatio = columnElement.dataset.widthRatio;

    // Remove placeholder if this is the first field in column
    const placeholder = columnElement.querySelector('.column-placeholder');
    if (placeholder) {
      placeholder.remove();
    }

    // Convert field ID to proper format if needed
    const cleanFieldId = fieldId.replace('field_', '');
    const extractedRowNumber = this.extractRowNumber(rowId);

    // Debounce auto-save to prevent excessive calls during rapid movements
    if (this.fieldDropTimeout) {
      clearTimeout(this.fieldDropTimeout);
    }

    this.fieldDropTimeout = setTimeout(() => {
      // Auto-save the new position
      this.triggerAutoSave(cleanFieldId, {
        layoutRow: extractedRowNumber,
        layoutCol: columnIndex + 1,
        widthRatio: parseInt(widthRatio),
        group: null // Could be enhanced to support groups
      });
    }, 150); // Wait 150ms to batch rapid drops
  }

  updateFieldPositions(columnElement) {
    const fields = columnElement.querySelectorAll('.layout-field');
    const rowElement = columnElement.closest('.layout-row');
    const columnIndex = Array.from(rowElement.querySelectorAll('.layout-column')).indexOf(columnElement);

    fields.forEach((field, index) => {
      const fieldId = field.dataset.fieldId.replace('field_', '');

      this.triggerAutoSave(fieldId, {
        layoutRow: this.extractRowNumber(rowElement.dataset.rowId),
        layoutCol: columnIndex + 1,
        position: index + 1
      });
    });
  }



  updateLayoutElementPositions() {
    console.log('🔄 Updating layout element positions...');

    // Debounce this function to prevent excessive database calls
    if (this.positionUpdateTimeout) {
      clearTimeout(this.positionUpdateTimeout);
    }

    this.positionUpdateTimeout = setTimeout(() => {
      this.performLayoutElementPositionsUpdate();
    }, 250); // Wait 250ms before executing
  }

  performLayoutElementPositionsUpdate() {
    // Get all layout elements (rows and section headers) in order
    const layoutElements = this.layoutAreaTarget.children;
    let globalPosition = 1; // Unified position counter for ALL elements
    const updateQueue = []; // Batch updates

    Array.from(layoutElements).forEach((element, index) => {
      if (element.classList.contains('section-header')) {
        // Queue section header position update
        const sectionId = element.dataset.sectionId;
        if (sectionId) {
          console.log(`📝 Section header at global position ${globalPosition}, sectionId: ${sectionId}`);
          updateQueue.push({
            type: 'section',
            id: sectionId,
            position: globalPosition
          });
          globalPosition++;
        }
      } else if (element.classList.contains('layout-row')) {
        // Extract the actual row number from the row element
        const actualRowNumber = this.extractRowNumber(element.dataset.rowId);

        // For field rows: DO NOT update layout_row or layout_col, only update position
        const fields = element.querySelectorAll('.layout-field');
        fields.forEach((field, fieldIndex) => {
          const fieldId = field.dataset.fieldId.replace('field_', '');

          console.log(`📝 Field ${fieldId} at global position ${globalPosition}, preserving row: ${actualRowNumber}`);
          updateQueue.push({
            type: 'field',
            id: fieldId,
            position: globalPosition
          });
          globalPosition++;
        });

        // Update the row title display
        const rowTitle = element.querySelector('.row-title');
        if (rowTitle) {
          rowTitle.textContent = `Row ${actualRowNumber}`;
        }
      }
    });

    // Execute all updates in batch
    this.executeBatchUpdates(updateQueue);
    console.log(`✅ Layout positions updated. Total elements: ${globalPosition - 1}`);
  }

  executeBatchUpdates(updateQueue) {
    // Process updates with minimal delay between each to prevent overwhelming the server
    updateQueue.forEach((update, index) => {
      setTimeout(() => {
        if (update.type === 'section') {
          // Only update section position if not currently being edited
          const sectionElement = document.querySelector(`[data-section-id="${update.id}"]`);
          const titleInput = sectionElement?.querySelector('.section-title');

          // Skip position updates if the title is being edited
          if (!titleInput || document.activeElement !== titleInput) {
            this.triggerSectionHeaderPositionUpdate(update.id, update.position);
          } else {
            console.log(`⏸️ Skipping section ${update.id} position update - title being edited`);
          }
        } else if (update.type === 'field') {
          this.triggerAutoSave(update.id, {
            position: update.position
          });
        }
      }, index * 10); // Stagger updates by 10ms each
    });
  }

  triggerAutoSave(fieldId, layoutData) {
    // Access the layout-autosave controller via Stimulus outlets
    if (this.hasLayoutAutosaveOutlet) {
      console.log('✅ Found layout-autosave outlet, triggering save');
      this.layoutAutosaveOutlet.triggerAutoSave(fieldId, layoutData);
    } else {
      console.warn('❌ Layout autosave outlet not found');
      // Fallback: make direct HTTP request
      this.makeDirectAutoSaveRequest(fieldId, layoutData);
    }
  }

  async makeDirectAutoSaveRequest(fieldId, layoutData) {
    console.log('🔄 Making direct auto-save request for field:', fieldId);

    const formData = new FormData();
    formData.append('field_id', fieldId);

    if (layoutData.layoutRow !== undefined) {
      formData.append('layout_row', layoutData.layoutRow);
    }
    if (layoutData.layoutCol !== undefined) {
      formData.append('layout_col', layoutData.layoutCol);
    }
    if (layoutData.widthRatio !== undefined) {
      formData.append('width_ratio', layoutData.widthRatio);
    }
    if (layoutData.group !== undefined) {
      formData.append('group', layoutData.group || '');
    }
    if (layoutData.position !== undefined) {
      formData.append('position', layoutData.position);
    }

    formData.append('authenticity_token', this.getAuthenticityToken());

    try {
      const response = await fetch(this.autoSaveUrlValue, {
        method: 'PATCH',
        body: formData,
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (response.ok) {
        console.log('✅ Direct auto-save successful');
        this.showAutoSaveSuccess();
      } else {
        console.error('❌ Direct auto-save failed:', response.status);
        this.showAutoSaveError();
      }
    } catch (error) {
      console.error('❌ Direct auto-save error:', error);
      this.showAutoSaveError();
    }
  }

  showAutoSaveSuccess() {
    this.updateSaveIndicator('✓ Auto-saved', 'text-success');
    setTimeout(() => {
      this.updateSaveIndicator('', '');
    }, 3000);
  }

  showAutoSaveError() {
    this.updateSaveIndicator('❌ Save failed', 'text-danger');
    setTimeout(() => {
      this.updateSaveIndicator('', '');
    }, 3000);
  }

  updateSaveIndicator(message, cssClass) {
    const indicator = document.querySelector('.auto-save-status');
    if (indicator) {
      indicator.textContent = message;
      indicator.className = `auto-save-status ${cssClass}`;
    }
  }

  getAuthenticityToken() {
    const tokenElement = document.querySelector('meta[name="csrf-token"]');
    return tokenElement ? tokenElement.content : '';
  }

  extractRowNumber(rowId) {
    const match = rowId.match(/row_(\d+)/);
    return match ? parseInt(match[1]) : 1;
  }

  showTemplateSelector(event) {
    const rowId = event.currentTarget.dataset.rowId;
    const rowElement = document.querySelector(`[data-row-id="${rowId}"]`);

    if (rowElement) {
      const templateSelector = rowElement.querySelector('.template-selector');
      if (templateSelector) {
        templateSelector.style.display = templateSelector.style.display === 'none' ? 'block' : 'none';
      }
    }
  }

  deleteRow(event) {
    const rowId = event.currentTarget.dataset.rowId;
    const rowElement = document.querySelector(`[data-row-id="${rowId}"]`);

    if (!rowElement) {
      console.error(`❌ Row element not found for ID: ${rowId}`);
      return;
    }

    // Get all fields in this row
    const fields = rowElement.querySelectorAll('.layout-field');
    const fieldCount = fields.length;

    // Create appropriate confirmation message
    let confirmMessage;
    if (fieldCount === 0) {
      confirmMessage = 'Are you sure you want to delete this empty row?';
    } else {
      confirmMessage = `Are you sure you want to delete this row and move ${fieldCount} field(s) back to the field palette?`;
    }

    if (confirm(confirmMessage)) {
      console.log(`🗑️ Deleting row ${rowId} with ${fieldCount} fields`);

      // If there are fields, move them back to the field palette
      if (fieldCount > 0) {
        fields.forEach(field => {
          const fieldId = field.dataset.fieldId.replace('field_', '');
          console.log(`📤 Moving field ${fieldId} back to palette`);

          // Remove field from layout (set layout position to null)
          this.triggerAutoSave(fieldId, {
            layoutRow: null,
            layoutCol: null,
            widthRatio: 12,
            position: null
          });
        });
      }

      // Remove the row from DOM immediately for better UX
      rowElement.remove();

      // Update positions of remaining elements
      this.updateLayoutElementPositions();

      // Show initial dropzone if no rows left
      const remainingRows = this.layoutAreaTarget.querySelectorAll('.layout-row');
      if (remainingRows.length === 0 && this.hasInitialDropzoneTarget) {
        this.initialDropzoneTarget.style.display = 'block';
        console.log('👀 Showing initial dropzone - no rows remaining');
      }

      // Show success feedback
      this.showDeleteRowFeedback(rowId, fieldCount);
    }
  }

  showDeleteRowFeedback(rowId, fieldCount) {
    // Create a temporary success message
    const feedback = document.createElement('div');
    feedback.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #28a745;
      color: white;
      padding: 12px 20px;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 500;
      z-index: 9999;
      box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
      animation: slideInRight 0.3s ease-out;
    `;

    if (fieldCount === 0) {
      feedback.textContent = `✅ Empty row deleted`;
    } else {
      feedback.textContent = `✅ Row deleted, ${fieldCount} field(s) moved to palette`;
    }

    document.body.appendChild(feedback);

    // Auto-remove feedback after 3 seconds
    setTimeout(() => {
      if (feedback.parentNode) {
        feedback.style.animation = 'slideOutRight 0.3s ease-in';
        setTimeout(() => feedback.remove(), 300);
      }
    }, 3000);
  }

  changeRowTemplate(event) {
    const rowId = event.currentTarget.dataset.rowId;
    const newTemplate = event.currentTarget.dataset.template;
    const rowElement = document.querySelector(`[data-row-id="${rowId}"]`);

    if (rowElement) {
      // Get current fields in the row
      const currentFields = Array.from(rowElement.querySelectorAll('.layout-field'));

      // Get new column configuration
      const newColumns = this.getColumnsForTemplate(newTemplate);

      // Update the row structure
      const rowContent = rowElement.querySelector('.row-content');
      rowContent.innerHTML = newColumns.map((colWidth, index) => `
        <div class="layout-column col-${colWidth}" data-column-id="${rowId}_col_${index + 1}" data-width-ratio="${colWidth}">
          <div class="layout-column-content">
            <div class="column-placeholder text-center text-muted py-3">
              <i class="fas fa-mouse-pointer"></i>
              <div>Drop fields here</div>
            </div>
          </div>
        </div>
      `).join('');

      // Re-initialize sortable for the new columns
      this.initializeColumnsInRow(rowElement);

      // Place existing fields in the first available columns
      currentFields.forEach((field, index) => {
        const targetColumnIndex = Math.min(index, newColumns.length - 1);
        const targetColumn = rowElement.querySelectorAll('.layout-column')[targetColumnIndex];

        if (targetColumn) {
          const columnContent = targetColumn.querySelector('.layout-column-content');
          const placeholder = columnContent.querySelector('.column-placeholder');
          if (placeholder) placeholder.remove();

          columnContent.appendChild(field);

          // Update field position
          const fieldId = field.dataset.fieldId.replace('field_', '');
          this.triggerAutoSave(fieldId, {
            layoutCol: targetColumnIndex + 1,
            widthRatio: newColumns[targetColumnIndex]
          });
        }
      });

      // Hide template selector
      const templateSelector = rowElement.querySelector('.template-selector');
      if (templateSelector) {
        templateSelector.style.display = 'none';
      }
    }
  }

  addSectionHeader(event) {
    console.log('📝 Adding section header...');

    const sectionId = `section_${Date.now()}`;
    const sectionHTML = this.createSectionHeaderHTML(sectionId, 'Section Title');

    // Add the section header to the layout area
    const sectionElement = document.createElement('div');
    sectionElement.innerHTML = sectionHTML;
    const sectionHeader = sectionElement.firstElementChild;

    // Insert at the end of the layout area (before the bottom template bar)
    this.layoutAreaTarget.appendChild(sectionHeader);

    // Focus on the title for immediate editing
    const titleInput = sectionHeader.querySelector('.section-title');
    if (titleInput) {
      titleInput.focus();
      titleInput.select();
    }

    console.log('✅ Section header added successfully');
  }

  createSectionHeaderHTML(sectionId, title) {
    return `
      <div class="section-header" data-section-id="${sectionId}">
        <div class="section-move-actions">
          <button class="section-move-btn" data-action="click->layout-builder#moveSectionUp"
                  data-section-id="${sectionId}" title="Move Up">
            <i class="fas fa-chevron-up"></i>
          </button>
          <button class="section-move-btn" data-action="click->layout-builder#moveSectionDown"
                  data-section-id="${sectionId}" title="Move Down">
            <i class="fas fa-chevron-down"></i>
          </button>
        </div>
        <input type="text" class="section-title" value="${title}"
               data-action="blur->layout-builder#saveSectionTitle change->layout-builder#saveSectionTitle"
               data-section-id="${sectionId}" />
        <div class="section-header-actions">
          <button class="section-delete-btn" data-action="click->layout-builder#deleteSectionHeader"
                  data-section-id="${sectionId}" title="Delete">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </div>
    `;
  }

  async saveSectionTitle(event) {
    const sectionId = event.currentTarget.dataset.sectionId;
    const title = event.currentTarget.value.trim();

    console.log('💾 Saving section title:', { sectionId, title });

    // Debounce the save to prevent conflicts with position updates
    if (this.sectionSaveTimeout) {
      clearTimeout(this.sectionSaveTimeout);
    }

    this.sectionSaveTimeout = setTimeout(async () => {
      try {
        const formData = new FormData();
        formData.append('section_id', sectionId);
        formData.append('title', title);
        formData.append('authenticity_token', this.getAuthenticityToken());

        const response = await fetch(`/event_doc_templates/${this.templateIdValue}/fields/save_section_header`, {
          method: 'POST',
          body: formData,
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (response.ok) {
          const result = await response.json();
          console.log('✅ Section title saved to database:', result);

          // Update DOM element with saved data
          const sectionElement = document.querySelector(`[data-section-id="${sectionId}"]`);
          if (sectionElement) {
            sectionElement.dataset.title = title;
          }

          // Show brief success indicator
          this.showSectionSavedFeedback(sectionElement);
        } else {
          console.error('❌ Failed to save section title:', response.statusText);
          const sectionElement = document.querySelector(`[data-section-id="${sectionId}"]`);
          this.showSectionSaveError(sectionElement);
        }
      } catch (error) {
        console.error('❌ Error saving section title:', error);
        const sectionElement = document.querySelector(`[data-section-id="${sectionId}"]`);
        this.showSectionSaveError(sectionElement);
      }
    }, 500); // Wait 500ms before saving to avoid conflicts
  }

  showSectionSavedFeedback(sectionElement) {
    const indicator = document.createElement('span');
    indicator.textContent = '✓ Saved';
    indicator.className = 'section-save-indicator text-success';
    indicator.style.cssText = 'position: absolute; right: 50px; top: 50%; transform: translateY(-50%); font-size: 12px; font-weight: bold;';

    sectionElement.style.position = 'relative';
    sectionElement.appendChild(indicator);

    setTimeout(() => {
      indicator.remove();
    }, 2000);
  }

  showSectionSaveError(sectionElement) {
    const indicator = document.createElement('span');
    indicator.textContent = '❌ Save failed';
    indicator.className = 'section-save-indicator text-danger';
    indicator.style.cssText = 'position: absolute; right: 50px; top: 50%; transform: translateY(-50%); font-size: 12px; font-weight: bold;';

    sectionElement.style.position = 'relative';
    sectionElement.appendChild(indicator);

    setTimeout(() => {
      indicator.remove();
    }, 3000);
  }

  getAuthenticityToken() {
    const tokenElement = document.querySelector('meta[name="csrf-token"]');
    return tokenElement ? tokenElement.content : '';
  }

  deleteSectionHeader(event) {
    const sectionId = event.currentTarget.dataset.sectionId;
    const sectionElement = document.querySelector(`[data-section-id="${sectionId}"]`);

    if (sectionElement && confirm('Are you sure you want to delete this section header?')) {
      sectionElement.remove();
      console.log('🗑️ Section header deleted:', sectionId);
    }
  }

  loadExistingLayout() {
    console.log('🏗️ Loading existing layout...');

    // First, check if there are already layout elements in the DOM (server-rendered)
    const existingRows = this.layoutAreaTarget.querySelectorAll('.layout-row');
    console.log(`📦 Found ${existingRows.length} existing rows in DOM`);

    if (existingRows.length > 0) {
      // We have server-rendered layout, just initialize drag-and-drop on existing elements
      console.log('🎯 Initializing drag-and-drop on server-rendered layout');

      existingRows.forEach((row, index) => {
        // Ensure row has proper ID if missing
        if (!row.dataset.rowId) {
          row.dataset.rowId = `existing_row_${index + 1}`;
          console.log(`🏷️ Added ID to existing row: ${row.dataset.rowId}`);
        }

        console.log('🔧 Setting up existing row:', row.dataset.rowId);
        this.initializeColumnsInRow(row);
      });

      // Update row counter to the highest existing row number
      const existingRowNumbers = this.getExistingRowNumbers();
      this.rowCounter = existingRowNumbers.length > 0 ? Math.max(...existingRowNumbers) : 0;
      console.log(`📊 Set row counter to: ${this.rowCounter} (based on existing rows: [${existingRowNumbers.join(', ')}])`);

      // Hide initial dropzone if we have existing rows
      if (this.hasInitialDropzoneTarget) {
        this.initialDropzoneTarget.style.display = 'none';
      }
    } else {
      // No existing rows, try to reconstruct from JSON data
      console.log('🔨 No existing rows found, attempting to reconstruct from JSON data');
      const fieldsData = this.getFieldsData();
      console.log('📊 Fields data loaded:', fieldsData);

      if (fieldsData && fieldsData.length > 0) {
        console.log(`📦 Found ${fieldsData.length} fields to layout`);
        this.reconstructLayoutFromFields(fieldsData);
      } else {
        console.log('📭 No existing fields found, showing empty layout');
      }
    }

    this.updateDebugPanel();
  }

  getFieldsData() {
    // Get fields data from JSON script tag
    const dataElement = document.querySelector('script[data-fields]');
    if (dataElement) {
      try {
        return JSON.parse(dataElement.textContent);
      } catch (e) {
        console.error('Error parsing fields data:', e);
        return [];
      }
    }
    return [];
  }

  reconstructLayoutFromFields(fields) {
    // Group fields by row
    const fieldsByRow = {};

    fields.forEach(field => {
      const row = field.layout_row || 1;
      if (!fieldsByRow[row]) {
        fieldsByRow[row] = [];
      }
      fieldsByRow[row].push(field);
    });

    // Create rows and place fields
    Object.keys(fieldsByRow).sort((a, b) => parseInt(a) - parseInt(b)).forEach(rowNum => {
      const rowFields = fieldsByRow[rowNum];

      // Determine template based on field columns
      const maxCol = Math.max(...rowFields.map(f => f.layout_col || 1));
      const template = this.determineTemplate(maxCol);

      this.addNewRowWithTemplate(template);

      // Place fields in appropriate columns
      rowFields.forEach(field => {
        this.placeFieldInLayout(field);
      });
    });
  }

  determineTemplate(maxColumns) {
    const templates = {
      1: '1x12',
      2: '2x6',
      3: '3x4',
      4: '4x3'
    };
    return templates[maxColumns] || '1x12';
  }

  placeFieldInLayout(field) {
    // Find the appropriate column and place the field
    const rowElements = this.layoutAreaTarget.querySelectorAll('.layout-row');
    const targetRow = rowElements[field.layout_row - 1];

    if (targetRow) {
      const columns = targetRow.querySelectorAll('.layout-column');
      const targetColumn = columns[field.layout_col - 1];

      if (targetColumn) {
        const fieldElement = this.createFieldElement(field);
        const columnContent = targetColumn.querySelector('.layout-column-content');

        // Remove placeholder
        const placeholder = columnContent.querySelector('.column-placeholder');
        if (placeholder) placeholder.remove();

        columnContent.appendChild(fieldElement);
      }
    }
  }

  createFieldElement(field) {
    const fieldDiv = document.createElement('div');
    fieldDiv.className = 'layout-field';
    fieldDiv.dataset.fieldId = `field_${field.id}`;
    fieldDiv.innerHTML = `
      <div class="field-header">
        <span class="field-label">${field.label}</span>
        <span class="field-type badge bg-info">${field.field_type}</span>
      </div>
 
    `;
    return fieldDiv;
  }

  generateFieldPreview(field) {
    // Simple field preview based on type
    switch (field.field_type) {
      case 'text':
        return `<input type="text" class="form-control" placeholder="${field.placeholder || ''}" disabled>`;
      case 'textarea':
        return `<textarea class="form-control" placeholder="${field.placeholder || ''}" disabled></textarea>`;
      case 'select':
        return `<select class="form-control" disabled><option>Select...</option></select>`;
      default:
        return `<div class="text-muted">${field.field_type} field</div>`;
    }
  }

  triggerSectionHeaderPositionUpdate(sectionId, position) {
    console.log(`💾 Updating section header position: ${sectionId} -> position ${position}`);

    // Use the same autosave mechanism but with section_id instead of field_id
    if (this.hasLayoutAutosaveOutlet) {
      this.layoutAutosaveOutlet.saveSectionHeaderPosition(sectionId, { position: position });
    } else {
      this.makeDirectSectionHeaderSaveRequest(sectionId, { position: position });
    }
  }

  async makeDirectSectionHeaderSaveRequest(sectionId, layoutData) {
    console.log('🔄 Making direct section header save request:', sectionId);

    const formData = new FormData();
    formData.append('section_id', sectionId);

    if (layoutData.position !== undefined) {
      formData.append('position', layoutData.position);
    }

    formData.append('authenticity_token', this.getAuthenticityToken());

    try {
      const response = await fetch(`/event_doc_templates/${this.templateIdValue}/fields/save_section_header`, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (response.ok) {
        console.log('✅ Section header position updated successfully');
      } else {
        console.error('❌ Failed to update section header position:', response.statusText);
      }
    } catch (error) {
      console.error('❌ Error updating section header position:', error);
    }
  }

  // Move section header up
  moveSectionUp(event) {
    const sectionId = event.currentTarget.dataset.sectionId;
    const sectionElement = document.querySelector(`[data-section-id="${sectionId}"]`);

    if (sectionElement) {
      this.moveElementUp(sectionElement, 'section header');
    }
  }

  // Move section header down
  moveSectionDown(event) {
    const sectionId = event.currentTarget.dataset.sectionId;
    const sectionElement = document.querySelector(`[data-section-id="${sectionId}"]`);

    if (sectionElement) {
      this.moveElementDown(sectionElement, 'section header');
    }
  }

  // Move row up
  moveRowUp(event) {
    const rowId = event.currentTarget.dataset.rowId;
    const rowElement = document.querySelector(`[data-row-id="${rowId}"]`);

    if (rowElement) {
      this.moveElementUp(rowElement, 'row');
    }
  }

  // Move row down
  moveRowDown(event) {
    const rowId = event.currentTarget.dataset.rowId;
    const rowElement = document.querySelector(`[data-row-id="${rowId}"]`);

    if (rowElement) {
      this.moveElementDown(rowElement, 'row');
    }
  }

  // Generic method to move element up
  moveElementUp(element, elementType) {
    const previousSibling = element.previousElementSibling;

    if (previousSibling) {
      console.log(`⬆️ Moving ${elementType} up`);
      element.parentNode.insertBefore(element, previousSibling);
      this.updateLayoutElementPositions();
      this.showMovementFeedback(element, 'moved up');
    } else {
      console.log(`⚠️ ${elementType} is already at the top`);
      this.showMovementFeedback(element, 'already at top');
    }
  }

  // Generic method to move element down
  moveElementDown(element, elementType) {
    const nextSibling = element.nextElementSibling;

    if (nextSibling) {
      console.log(`⬇️ Moving ${elementType} down`);
      element.parentNode.insertBefore(nextSibling, element);
      this.updateLayoutElementPositions();
      this.showMovementFeedback(element, 'moved down');
    } else {
      console.log(`⚠️ ${elementType} is already at the bottom`);
      this.showMovementFeedback(element, 'already at bottom');
    }
  }

  // Show visual feedback for movement
  showMovementFeedback(element, action) {
    // Add a temporary visual highlight
    element.classList.add('movement-highlight');

    // Create a temporary feedback indicator
    const feedback = document.createElement('span');
    feedback.className = 'movement-feedback';
    feedback.textContent = action;
    feedback.style.cssText = `
      position: absolute;
      top: 50%;
      right: 10px;
      transform: translateY(-50%);
      background: rgba(40, 167, 69, 0.9);
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: bold;
      z-index: 1000;
      animation: fadeInOut 2s ease-in-out;
    `;

    element.style.position = 'relative';
    element.appendChild(feedback);

    // Remove feedback and highlight after 2 seconds
    setTimeout(() => {
      element.classList.remove('movement-highlight');
      if (feedback.parentNode) {
        feedback.remove();
      }
    }, 2000);
  }

  cleanupSortables() {
    console.log('🧹 Cleaning up sortables...');

    let cleanedCount = 0;

    // Cleanup tracked sortable instances
    this.sortableInstances.forEach(sortable => {
      if (sortable.instance && typeof sortable.instance.destroy === 'function') {
        console.log(`🗑️ Destroying sortable: ${sortable.name}`);
        sortable.instance.destroy();
        cleanedCount++;
      }
    });


    console.log(`✅ Cleaned up ${cleanedCount} sortable instances`);
    this.sortableInstances = [];
  }

}